import { CommonModule } from '@angular/common';
import { Component, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LocalUserRoles } from '@buspro/shared/common';
import { OperationsApi, OperationsDto } from '@buspro/web/apis';
import { LocalUserDto } from '@buspro/web/apis';
import { ApiError } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NGXLogger } from 'ngx-logger';
import { MomentModule } from 'ngx-moment';
import { lastValueFrom } from 'rxjs';
import { AwaitingMediaWidgetComponent } from '../../components/awaiting-media-widget.component';

@Component({
  selector: 'buspro-admin-auditors-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzCardModule,
    NzIconModule,
    NzSpinModule,
    NzStatisticModule,
    NzTagModule,
    NzToolTipModule,
    MomentModule,
    AwaitingMediaWidgetComponent,
  ],
  templateUrl: './auditors-dashboard.component.html',
})
export class AuditorsDashboardComponent implements OnInit {
  private operationsApi = inject(OperationsApi);
  private router = inject(Router);
  private notification = inject(NzNotificationService);
  private logger = inject(NGXLogger);
  private ngxAuthService = inject(NgxAuthService);

  // Convert Observable to Signal for easier access
  authUser: Signal<LocalUserDto | null> = toSignal(this.ngxAuthService.state, { initialValue: null });

  // Query for next audit (earliest completed operation ready for audit)
  nextAuditQuery = injectQuery(() => ({
    queryKey: ['next-audit'],
    queryFn: async () => {
      const operations = await lastValueFrom(this.operationsApi.operationsControllerGetOperationsReadyForAudit());
      // Return the first operation (oldest/earliest) as the next audit
      return operations.length > 0 ? operations[0] : null;
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  }));

  // Query for current audit in progress
  currentAuditQuery = injectQuery(() => ({
    queryKey: ['my-current-audit'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetMyCurrentAudit()),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  }));

  // Query for audit stats
  auditStatsQuery = injectQuery(() => ({
    queryKey: ['my-audit-stats'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetMyAuditStats()),
    refetchInterval: 60000, // Auto-refresh every minute
  }));

  // Mutation to start audit process
  startAuditMutation = injectMutation(() => ({
    mutationFn: (operationId: string) =>
      // Body is required by the endpoint; this dashboard never overrides the audit order,
      // so it sends the empty one — matching operations-ready-for-audit-page, which passes
      // a reason only when the auditor actually gave one.
      lastValueFrom(this.operationsApi.operationsControllerStartAuditProcess({ id: operationId, body: {} })),
    onSuccess: (_data, operationId) => {
      this.notification.success('✅ Audit Started', 'Redirecting to audit screen...');

      // Refresh queries to update state
      this.nextAuditQuery.refetch();
      this.currentAuditQuery.refetch();
      this.auditStatsQuery.refetch();

      // Auto-redirect to audit detail page
      setTimeout(() => {
        this.router.navigate(['/audits', operationId, 'detail']);
      }, 500);
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to start audit:', error);

      const apiError = error as ApiError;
      const message = apiError.error?.detail || 'Failed to start audit process. Please try again.';

      this.notification.error('❌ Cannot Start Audit', message);
    },
  }));

  // Computed values
  nextAudit = computed(() => this.nextAuditQuery.data());
  currentAudit = computed(() => this.currentAuditQuery.data());
  auditStats = computed(() => this.auditStatsQuery.data());
  hasAuditInProgress = computed(() => !!this.currentAudit());
  canStartNextAudit = computed(() => {
    return (
      !!this.nextAudit() &&
      !this.startAuditMutation.isPending() &&
      !this.hasAuditInProgress() &&
      this.isNextAuditEarliest()
    );
  });

  /**
   * Check if the next audit is the earliest (first in queue)
   */
  isNextAuditEarliest(): boolean {
    const nextAudit = this.nextAudit();
    if (!nextAudit) {
      return false;
    }

    // The nextAudit is already the first from the API (sorted by auditReadyAt ASC)
    // So it's always the earliest
    return true;
  }

  /**
   * Get tooltip message for start audit button
   */
  getStartAuditTooltip(): string {
    if (this.startAuditMutation.isPending()) {
      return 'Processing...';
    }

    if (this.hasAuditInProgress()) {
      const currentAudit = this.currentAudit();
      return `Complete your current audit (${currentAudit?.auditNumber}) first`;
    }

    if (!this.nextAudit()) {
      return 'No operations ready for audit';
    }

    return 'Start auditing this operation';
  }

  /**
   * Get waiting time from auditReadyAt
   */
  getWaitingTime(readyAt: Date | string | null | undefined): string {
    if (!readyAt) {
      return 'Unknown';
    }
    return moment(readyAt).fromNow();
  }

  /**
   * Start audit for the next operation
   */
  startNextAudit(): void {
    const nextAudit = this.nextAudit();
    if (!nextAudit || !this.canStartNextAudit()) {
      return;
    }

    this.startAuditMutation.mutate(nextAudit.id);
  }

  /**
   * Resume current audit in progress
   */
  resumeAudit(operationId: string): void {
    this.router.navigate(['/audits', operationId, 'detail']);
  }

  /**
   * Navigate to ready for audits page
   */
  viewAllReadyAudits(): void {
    this.router.navigate(['/audits/ready']);
  }

  /**
   * Navigate to my audits page
   */
  viewMyAudits(): void {
    this.router.navigate(['/audits/my-audits']);
  }

  /**
   * Refresh all data
   */
  refreshData(): void {
    this.nextAuditQuery.refetch();
    this.currentAuditQuery.refetch();
    this.auditStatsQuery.refetch();
  }

  ngOnInit(): void {
    // Check if user is NOT an auditor - redirect them to main dashboard
    const currentUser = this.authUser();
    if (currentUser) {
      const role = currentUser.role;
      if (role !== LocalUserRoles.LOCAL_AUDITOR) {
        this.logger.info('Non-auditor detected, redirecting to main dashboard');
        this.router.navigateByUrl('/dashboard');
        return;
      }
    }
  }
}
